// Selective invalidation logic (ARCHITECTURE.md §12.6).
//
// Compares current content hashes / versions against the values recorded in the
// store and decides what must be re-analyzed. The functions are pure queries;
// they never mutate the store. `getInvalidationPlan` aggregates the individual
// checks into an ordered list of actions a caller can execute.

import type { StoreHandle } from "./types.js";

// Metadata keys (§12.4)
const NAVIGATION_HASH_KEY = "navigation_hash";
const MANIFEST_HASH_PREFIX = "manifest_hash:";
const ANALYZER_VERSION_KEY = "analyzer_version";

export type FileChangeState = "unchanged" | "changed" | "new" | "deleted";

/**
 * Classify a file against the store. `currentHash` is the freshly-computed hash
 * of the file's content, or `null` when the file no longer exists on disk.
 */
export function checkFileChanged(
  db: StoreHandle,
  filePath: string,
  currentHash: string | null,
): FileChangeState {
  const row = db.backend.getFileByPath(filePath);
  if (row) {
    if (currentHash === null) return "deleted";
    return row.hash === currentHash ? "unchanged" : "changed";
  }
  // Not in the store: it is new if it exists on disk, otherwise nothing to do.
  return currentHash === null ? "unchanged" : "new";
}

/** True if a manifest's content differs from the stored hash (or is unknown). */
export function checkManifestChanged(
  db: StoreHandle,
  manifestPath: string,
  currentHash: string,
): boolean {
  const stored = db.backend.getMetadata(MANIFEST_HASH_PREFIX + manifestPath);
  return stored !== currentHash;
}

/** True if the Navigation Index hash differs from the stored hash. */
export function checkNavigationIndexChanged(
  db: StoreHandle,
  currentHash: string,
): boolean {
  return db.backend.getMetadata(NAVIGATION_HASH_KEY) !== currentHash;
}

/** True if the analyzer version differs from the stored version. */
export function checkAnalyzerVersionChanged(
  db: StoreHandle,
  currentVersion: string,
): boolean {
  return db.backend.getMetadata(ANALYZER_VERSION_KEY) !== currentVersion;
}

// ---------------------------------------------------------------------------
// Invalidation plan
// ---------------------------------------------------------------------------

export interface InvalidationChanges {
  /** Source files with their current hash (null = deleted on disk). */
  files?: { path: string; hash: string | null }[];
  /** Package manifests with their current hash. */
  manifests?: { path: string; hash: string }[];
  /** Current Navigation Index hash, if it should be checked. */
  navigationHash?: string;
  /** Current analyzer version, if it should be checked. */
  analyzerVersion?: string;
}

export type InvalidationActionKind =
  | "reanalyze-file"
  | "delete-file"
  | "rebuild-manifest"
  | "rebuild-mapping"
  | "full-rebuild";

export interface InvalidationAction {
  kind: InvalidationActionKind;
  /** File path / manifest path the action targets, when applicable. */
  target?: string;
  reason: string;
}

/**
 * Produce the ordered set of actions needed to bring the store up to date.
 *
 * An analyzer-version mismatch supersedes everything: it returns a single
 * `full-rebuild` action (§12.6), since stored data may be structurally stale.
 */
export function getInvalidationPlan(
  db: StoreHandle,
  changes: InvalidationChanges,
): InvalidationAction[] {
  if (
    changes.analyzerVersion !== undefined &&
    checkAnalyzerVersionChanged(db, changes.analyzerVersion)
  ) {
    return [
      {
        kind: "full-rebuild",
        reason: "analyzer version changed; full rebuild required",
      },
    ];
  }

  const actions: InvalidationAction[] = [];

  for (const file of changes.files ?? []) {
    const state = checkFileChanged(db, file.path, file.hash);
    if (state === "new" || state === "changed") {
      actions.push({
        kind: "reanalyze-file",
        target: file.path,
        reason: `file ${state}`,
      });
    } else if (state === "deleted") {
      actions.push({
        kind: "delete-file",
        target: file.path,
        reason: "file deleted",
      });
    }
  }

  for (const manifest of changes.manifests ?? []) {
    if (checkManifestChanged(db, manifest.path, manifest.hash)) {
      actions.push({
        kind: "rebuild-manifest",
        target: manifest.path,
        reason: "manifest changed",
      });
    }
  }

  if (
    changes.navigationHash !== undefined &&
    checkNavigationIndexChanged(db, changes.navigationHash)
  ) {
    actions.push({
      kind: "rebuild-mapping",
      reason: "navigation index changed",
    });
  }

  return actions;
}
